import { useUnit } from 'effector-react'
import React, {
  useEffect,
  useState,
  ReactNode,
  useCallback,
  useMemo
} from 'react'
import {
  useEvaluateApiV1InterviewsInterviewIdEvaluatePost,
  useGetVacancyUiSettingsApiV1VacanciesVacancyIdUiSettingsGet
} from '../../../../api/endpoints/openapi-generated'
import { ResultsProps } from '../types'
import {
  GetInterviewEvaluationResponse,
  InterviewEvaluationResource,
  InterviewFinishInitiator
} from '../../../../api/endpoints/openapi-generated.schemas'
import { BubbleMessages } from '../../../../components/bubble-messages'
import { useAnalytics } from '../../../../hooks/use-analytics'
import { captureError } from '../../../../utils/sentry'
import { withSentryOnError } from '../../../../utils/sentry/with-sentry-on-error'
import { Route } from '../../../../routes/$companyAlias/$positionAlias'
import { Progress } from 'aivy-design-system/components/ui/progress'
import { useResultsProgress } from '../../hooks/use-results-progress'
import { usePreventTabClose } from '../../../../hooks/use-prevent-close-tab'
import { useTranslation } from 'react-i18next'
import { videoChunksUploadService } from '../call/video-chunk-upload.service'
import { videoRecorderService } from '../call/video-recorder.service'
import { devicesService } from '../../../devices.service'

export const Results: React.FC<ResultsProps> = ({
  vacancy,
  interviewId,
  variables
}) => {
  const { t } = useTranslation()

  const { capture } = useAnalytics()
  const { progress, finish, isFinished } = useResultsProgress()
  const { disable: disabledTabLocker } = usePreventTabClose()

  const queue = useUnit(videoChunksUploadService.$queue)
  const recorder = useUnit(videoRecorderService.$recorder)

  const { initiator } = Route.useSearch()

  const [results, setResults] = useState<InterviewEvaluationResource>()
  const [waitSettle, setWaitSettle] = useState<boolean>(true)

  const { data: vacancyUiSettings, refetch: refetchUISettings } =
    useGetVacancyUiSettingsApiV1VacanciesVacancyIdUiSettingsGet(vacancy.id, {
      query: {
        throwOnError: withSentryOnError({
          type: 'query',
          queryKey: ['getUISettings', vacancy.id]
        })
      }
    })

  const bubbleMessages = useMemo(() => {
    if (!vacancyUiSettings || !results) return

    const uiSettings = vacancyUiSettings.data.settings[0]?.results_screen

    if (results.evaluation?.is_success) {
      return uiSettings.bubble_messages_success
    } else {
      return uiSettings.bubble_messages_fail
    }
  }, [results, vacancyUiSettings])

  useEffect((): VoidFunction => {
    const timer = window.setTimeout(() => {
      setWaitSettle(false)
    }, 2_000)

    return (): void => {
      window.clearTimeout(timer)
    }
  }, [])

  useEffect(() => {
    if (waitSettle) return

    devicesService.microphone.destroy()
    devicesService.camera.destroy()
  }, [waitSettle])

  useEffect(() => {
    if (!vacancyUiSettings) {
      return
    }

    if (vacancyUiSettings.data.settings.length < 1) {
      captureError(new Error('No results screen settings found'), {
        vacancy_id: vacancy.id
      })
      return
    }
  }, [vacancy.id, vacancyUiSettings])

  useEffect(() => {
    capture('page_results_open', {
      vacancy_id: vacancy.id,
      interview_id: interviewId
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!results) return

    finish(300)
    disabledTabLocker()
  }, [results, finish, disabledTabLocker])

  const handleSetResults = useCallback(
    (results: InterviewEvaluationResource): void => {
      setResults(results)
      void refetchUISettings()
    },
    [refetchUISettings]
  )

  const doSendEvaluate = !queue.length && !recorder && !waitSettle
  const showProgress = !isFinished || !bubbleMessages

  // console.log('doSendEvaluate', doSendEvaluate)
  // console.log('showProgress', showProgress)
  // console.log('isFinished', isFinished)
  // console.log('bubbleMessages', bubbleMessages)
  // console.log('progress', progress)

  return (
    <main className="w-full min-h-screen bg-muted flex flex-col items-center pt-[66px] px-6">
      {doSendEvaluate && (
        <ResultsEvaluate
          interviewId={interviewId}
          initiator={initiator}
          setResults={handleSetResults}
        />
      )}

      <header className="flex flex-col gap-4 mb-8 w-full max-w-[512px]">
        <img
          className="size-[209px] self-start"
          src="/assets/images/sokolov-results.png"
        />

        <h1 className="text-4xl font-black">
          <div dangerouslySetInnerHTML={{ __html: t('results.header') }} />
        </h1>
      </header>

      {showProgress && (
        <section className="w-full max-w-[512px]">
          <p className="px-4 py-3 bg-white rounded-lg mb-3">
            <b>{t('results.wait_warning_bold')}</b> {t('results.wait_warning')}
          </p>

          <div className="px-4 py-3 bg-white rounded-lg">
            <span className="block text-center mb-3">
              {t('results.loading')}
            </span>
            <Progress value={progress} />
          </div>
        </section>
      )}

      {!showProgress && (
        <section className="w-full max-w-[512px] -mt-[40px]">
          <BubbleMessages
            phoneCountryCode="+7"
            bubbleMessages={bubbleMessages}
            interviewId={null}
            variables={
              variables
              // candidateName: interview.data.candidate_name
            }
            vacancy={vacancy}
          />
        </section>
      )}
    </main>
  )
}

type ResultsEvaluateProps = {
  interviewId: string
  initiator: InterviewFinishInitiator
  setResults: (results: InterviewEvaluationResource) => void
}

function ResultsEvaluate({
  interviewId,
  initiator,
  setResults
}: ResultsEvaluateProps): ReactNode {
  const { mutate: evaluateInterview } =
    useEvaluateApiV1InterviewsInterviewIdEvaluatePost()

  useEffect(() => {
    evaluateInterview(
      {
        interviewId: interviewId,
        data: {
          initiator: initiator ?? InterviewFinishInitiator.interviewer
        }
      },
      {
        onSuccess: (response: GetInterviewEvaluationResponse) => {
          setResults(response.data)
        },
        onError: () => {
          setResults({
            interview_id: '',
            evaluation: {
              is_success: false,
              total_score: 0
            }
          })
          withSentryOnError({
            type: 'mutation',
            mutationKey: ['evaluateInterview', interviewId]
          })
        }
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return null
}
